import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { allPosts } from 'content-collections'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_public/blog/')({
  staticData: {
    title: 'Blog — PhoneSalad',
    description: 'Tips, guides, and comparisons for making affordable international calls. Learn how to save money calling abroad and get the most from PhoneSalad.',
    keywords: 'international calls blog, cheap calling tips, voip guides, calling abroad',
    sitemap: { priority: '0.7', changefreq: 'weekly' },
  },
  loader: () => {
    // Sort posts by publishedAt date
    return allPosts.sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    )
  },
  component: BlogPage,
})

function formatDate(dateStr: string) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function BlogPage() {
  const articles = Route.useLoaderData()
  const { t } = useTranslation('marketing')

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8 pt-20">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-foreground mb-2">{t('blog.title')}</h1>
            <p className="text-lg text-muted-foreground">
              {t('blog.description')}
            </p>
          </div>
          <div className="grid gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to="/blog/$slug"
                params={{ slug: article.slug }}
                className="group block rounded-xl border bg-card p-6 md:p-8 hover:border-primary/30 hover:shadow-md transition-all"
              >
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-3">
                  <span className="bg-primary/10 text-primary px-2.5 py-0.5 rounded-full font-medium">
                    {article.category}
                  </span>
                  <span>{formatDate(article.publishedAt)}</span>
                  <span>·</span>
                  <span>{article.readingTime}</span>
                </div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {article.title}
                </h2>
                <p className="text-muted-foreground mb-4">{article.description}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                  {t('blog.readArticle')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
